// # Libraries Guide Component
import React, { useEffect, useRef, useState } from 'react';
import { 
  RocketOutlined, 
  BookOutlined, 
  CheckCircleOutlined,
  BulbOutlined,
  QuestionCircleOutlined 
} from '@ant-design/icons';
import DashboardHeader from '../../../components/DashboardHeader';
import Header from '../../../landing/components/header';  // # Import regular header
import DashboardFooter from '../../../components/DashboardFooter';
import './guide_libraries.css';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../../auth/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import cacheManager from '../../../auth/cache_manager';
import { useAuthStore } from '../../../auth/state';  // # Import auth store

const LibrariesGuide = () => {
  const navigate = useNavigate();
  const sectionRefs = useRef([]);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const { isAuthenticated } = useAuthStore();  // # Get auth state

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (auth.currentUser?.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, []);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (auth.currentUser?.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', auth.currentUser.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${auth.currentUser.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const handleIntersect = (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
          
          const qaItems = entry.target.querySelectorAll('.gde_lib_qa-item');
          qaItems.forEach((item, i) => {
            const baseDelay = 0.1;
            const staggerDelay = i * 0.15;
            item.style.setProperty('--delay', `${baseDelay + staggerDelay}s`);
            item.style.opacity = '1';
            item.style.transform = 'translateX(0)';
            item.classList.add('gde_lib_animate');
          });
          
          observer.unobserve(entry.target);
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersect, observerOptions);
    sectionRefs.current.forEach(section => section && observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const sections = [
    {
      title: 'Introduction to TEMP-U',
      icon: <BookOutlined className="gde_lib_section-icon" />,
      questions: [
        {
          q: 'What is TEMP-U Library?',
          a: 'TEMP-U Library is a comprehensive and well-structured USMLE library for Step 1 and Step 2, designed to be the only resource you need for effective studying.'
        },
        {
          q: 'What makes TEMP-U Library unique?',
          a: '<ul><li><strong>All-in-One:</strong> Covers all high-yield content ever mentioned in any question bank or review source in one place</li><li><strong>Highly Organized:</strong> Clear headings, structured topics, and numbered steps for easy navigation</li><li><strong>Engaging:</strong> Highlights, colors, and embedded Q&As for active learning</li><li><strong>Visual Learning:</strong> Extensive media, diagrams, and clinical images to reinforce understanding</li><li><strong>Expert Insights:</strong> Notes from high-achieving tutors to provide top-tier guidance</li></ul>'
        },
        {
          q: 'Why use TEMP-U over other resources?',
          a: 'TEMP-U saves time, enhances retention, and makes studying enjoyable by integrating the most relevant, high-yield content into an interactive and visually rich format.<div class="gde_lib_note">Unlike First Aid for Step 1, it is more structured and engaging, and for Step 2, it provides the best available study tool in a field lacking strong resources.</div>'
        }
      ]
    },
    {
      title: 'USMLE Step 1 Study Guide',
      icon: <CheckCircleOutlined className="gde_lib_section-icon" />,
      questions: [
        {
          q: 'How should I use TEMP-U for Step 1?',
          a: 'Follow these key strategies:<ul><li><strong>Understand first:</strong> Start with videos to grasp the topic</li><li><strong>Study & memorize:</strong> Focus on all key information</li><li><strong>Pay attention to highlights & callouts:</strong> These are crucial details</li><li><strong>Use images & videos:</strong> Reinforce concepts visually</li><li><strong>Toggles:</strong> These provide extra information (least priority)</li><li><strong>Look for the 🆚 symbol:</strong> Essential for differential diagnoses (DDs)</li></ul><div class="gde_lib_note">Look for the clues to diagnose: essential to recognize the buzzwords that can guide you to the diagnosis.</div>'
        }
      ]
    },
    {
      title: 'USMLE Step 2 Study Guide',
      icon: <BulbOutlined className="gde_lib_section-icon" />,
      questions: [
        {
          q: 'How should I use TEMP-U for Step 2?',
          a: 'Follow these strategies:<ul><li><strong>Extract key takeaways:</strong> Focus on the most important points</li><li><strong>Understand the big picture:</strong> Comprehend the overall concept</li><li><strong>Prioritize text:</strong> This is your main study tool</li><li><strong>Use images & UWorld tables (UWT) as aids:</strong> They support understanding</li><li><strong>Look for the 🆚 symbol:</strong> Essential for differential diagnoses (DDs)</li><li><strong>Use Clutches & Puzzles:</strong> They break down complex topics into concise notes</li><li><strong>Toggles:</strong> These provide extra information (least priority)</li></ul><div class="gde_lib_note">TEMP-U is designed to simplify and elevate your USMLE prep—no need to look elsewhere!</div>'
        }
      ]
    }
  ];

  return (
    <div className={`gde_lib_page-container ${isDarkMode ? 'gde_lib_dark-mode' : ''}`}>
      {isAuthenticated ? (
        <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      ) : (
        <Header />
      )}

      <div className="gde_lib_content-container">
        <div className="gde_lib_header">
          <div className="gde_lib_header-content">
            <div className="gde_lib_header-title">
              <div className="gde_lib_icon-container">
                <BookOutlined style={{ fontSize: '3rem', color: '#1a365d' }} />
              </div>
              <h1>TEMP-U Library Guide</h1>
            </div>
            <div className="gde_lib_header-subtitle">
              <p>Your comprehensive guide to the Ultimate USMLE Library</p>
            </div>
          </div>
        </div>

        <div className="gde_lib_sections">
          {sections.map((section, index) => (
            <div 
              key={index} 
              className="gde_lib_section"
              ref={el => sectionRefs.current[index] = el}
            >
              <div className="gde_lib_section-header">
                <div className="gde_lib_icon-container">
                  {section.icon}
                </div>
                <h2>{section.title}</h2>
              </div>
              
              <div className="gde_lib_qa-list">
                {section.questions.map((qa, qaIndex) => (
                  <div key={qaIndex} className="gde_lib_qa-item">
                    <div className="gde_lib_question">
                      <strong>Q{qaIndex + 1}:</strong> {qa.q}
                    </div>
                    <div className="gde_lib_answer" 
                         dangerouslySetInnerHTML={{ __html: qa.a }}>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          
          <div className="gde_lib_cta-section">
            <div className="gde_lib_cta-content">
              <div className="gde_lib_cta-icon">
                <RocketOutlined />
              </div>
              <h2>Ready to Excel in Your USMLE Journey?</h2>
              <p>Access our comprehensive TEMP-U library and start your path to success</p>
              <button 
                className="gde_lib_cta-button"
                onClick={() => navigate('/products')}
              >
                Explore TEMP-U Library
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="gde_lib_footer-wrapper">
        <DashboardFooter isDarkMode={isDarkMode} />
      </div>
    </div>
  );
};

export default LibrariesGuide;
